// NOTE: For new code, prefer the `useResendRequest` hook from
// `@/components/treatment/v3/request/resend/useResendRequest` which calls
// the dedicated resend endpoint with structured reason tracking.
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import type { RetryTreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import type { AlternateContact } from '@/types/treatment-request-alternates';
import { canonicalizeHandleType } from '@/utils/treatment-request-alternates';

interface UseRequestResendActionsParams {
  matterId: string;
  providerId: string;
  requestId: string;
  onRequestUpdate?: () => Promise<void> | void;
}

interface UseRequestResendActionsResult {
  resendViaCurrentContact: () => Promise<void>;
  resendWithAlternate: (alternate: AlternateContact) => Promise<void>;
  isResendingCurrentContact: boolean;
  activeAlternateKey: string | null;
  isAnyResendInFlight: boolean;
}

const buildAlternateKey = (alternate: AlternateContact) =>
  `${canonicalizeHandleType(alternate.handle_type)}::${alternate.value.trim()}`;

const DEFAULT_RETRY_PAYLOAD: RetryTreatmentRecordRequestSchema = {
  source: 'current_channel',
};

const mapHandleTypeForMutation = (
  alternate: AlternateContact,
): 'fax' | 'mailing_address' | null => {
  const canonical = canonicalizeHandleType(alternate.handle_type);

  if (canonical === 'FAX') {
    return 'fax';
  }

  if (canonical === 'MAILING_ADDRESS') {
    return 'mailing_address';
  }

  return null;
};

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'error' in (error.data as Record<string, unknown>)
  ) {
    const data = error.data as { error?: string };
    if (data?.error) {
      return data.error;
    }
  }

  return 'Something went wrong. Please try again.';
};

export const useRequestResendActions = ({
  matterId,
  providerId,
  requestId,
  onRequestUpdate,
}: UseRequestResendActionsParams): UseRequestResendActionsResult => {
  const dispatch = useDispatch();

  const [retryExistingContact, { isLoading: isRetryExistingLoading }] =
    treatmentRecordRequestService.useRetryExistingContactMutation();

  const [acceptAlternate, { isLoading: isAcceptAlternateLoading }] =
    treatmentRecordRequestService.useAcceptAlternateContactMutation();

  const [activeAlternateKey, setActiveAlternateKey] = useState<string | null>(
    null,
  );

  const [isResendingCurrentContact, setIsResendingCurrentContact] =
    useState(false);

  const handlePostSuccess = useCallback(
    async (newRequestId: string | string, successMessage: string) => {
      toast.success(successMessage);

      dispatch(
        treatmentRecordRequestService.util.invalidateTags([
          { type: 'record-request', id: requestId },
          { type: 'record-requests', id: providerId },
        ]),
      );

      if (onRequestUpdate) {
        await onRequestUpdate();
      }
    },
    [dispatch, onRequestUpdate, providerId, requestId],
  );

  const resendViaCurrentContact = useCallback(async () => {
    if (isResendingCurrentContact) {
      return;
    }

    setIsResendingCurrentContact(true);

    try {
      const result = await retryExistingContact({
        matterId,
        providerId,
        requestId,
        retryTreatmentRecordRequestSchema: DEFAULT_RETRY_PAYLOAD,
      }).unwrap();

      await handlePostSuccess(
        result.new_request_id,
        'Request resent via current contact.',
      );
    } catch (error) {
      toast.error(extractErrorMessage(error));
      throw error;
    } finally {
      setIsResendingCurrentContact(false);
    }
  }, [
    handlePostSuccess,
    isResendingCurrentContact,
    matterId,
    providerId,
    requestId,
    retryExistingContact,
  ]);

  const resendWithAlternate = useCallback(
    async (alternate: AlternateContact) => {
      const key = buildAlternateKey(alternate);

      if (activeAlternateKey === key) {
        return;
      }

      const handleType = mapHandleTypeForMutation(alternate);
      if (!handleType) {
        toast.error(
          'Accept & Resend is currently supported for fax numbers or mailing addresses only.',
        );
        return;
      }

      setActiveAlternateKey(key);
      try {
        const result = await acceptAlternate({
          matterId,
          providerId,
          requestId,
          acceptAlternateContactSchema: {
            handle_type: handleType,
            value: alternate.value,
            label: alternate.label,
            confidence: alternate.confidence ?? undefined,
          },
        }).unwrap();

        await handlePostSuccess(
          result.new_request_id,
          'Alternate contact accepted. New request created.',
        );
      } catch (error) {
        toast.error(extractErrorMessage(error));
        throw error;
      } finally {
        setActiveAlternateKey(null);
      }
    },
    [
      acceptAlternate,
      activeAlternateKey,
      handlePostSuccess,
      matterId,
      providerId,
      requestId,
    ],
  );

  const isAnyResendInFlight = useMemo(
    () =>
      isResendingCurrentContact ||
      isRetryExistingLoading ||
      Boolean(activeAlternateKey) ||
      isAcceptAlternateLoading,
    [
      activeAlternateKey,
      isAcceptAlternateLoading,
      isResendingCurrentContact,
      isRetryExistingLoading,
    ],
  );

  return {
    resendViaCurrentContact,
    resendWithAlternate,
    isResendingCurrentContact:
      isResendingCurrentContact || isRetryExistingLoading,
    activeAlternateKey,
    isAnyResendInFlight,
  };
};
